import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CarouselArrow } from "./CarouselArrow.js";
import type { Photo } from "../data/types.js";

interface Props {
  photos: Photo[];
  initialIndex: number;
  onClose: () => void;
}

export function PhotoLightbox({ photos, initialIndex, onClose }: Props) {
  const [index, setIndex] = useState(initialIndex);
  const photo = photos[index];

  const setWrapped = (i: number) => {
    setIndex(((i % photos.length) + photos.length) % photos.length);
  };

  return (
    <motion.div
      className="fixed inset-0 z-[100] bg-black flex flex-col"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      <div
        className="flex items-center justify-between px-4 py-3 z-10"
        style={{ paddingTop: "env(safe-area-inset-top, 12px)" }}
      >
        <span className="text-white/60 text-xs">
          {index + 1} / {photos.length}
        </span>
        <button
          onClick={onClose}
          className="text-white/80 hover:text-white text-sm font-medium p-1"
        >
          ✕
        </button>
      </div>
      <div className="flex-1 relative overflow-hidden">
        {photos.length > 1 && (
          <>
            <CarouselArrow
              direction="left"
              onClick={() => setWrapped(index - 1)}
              variant="dark"
            />
            <CarouselArrow
              direction="right"
              onClick={() => setWrapped(index + 1)}
              variant="dark"
            />
          </>
        )}
        <AnimatePresence initial={false} mode="popLayout">
          <motion.div
            key={index}
            initial={{ scale: 0.98, opacity: 0.6 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            className="absolute inset-0 flex items-center justify-center"
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.4}
            onDragEnd={(_e, info) => {
              if (info.offset.x < -30 || info.velocity.x < -200) {
                setWrapped(index + 1);
              } else if (info.offset.x > 30 || info.velocity.x > 200) {
                setWrapped(index - 1);
              }
            }}
          >
            <img
              src={photo.url.replace("w_1200", "w_1800")}
              alt={photo.alt}
              className="max-w-full max-h-full object-contain select-none"
              draggable={false}
            />
          </motion.div>
        </AnimatePresence>
      </div>
      {photos.length > 1 && (
        <div
          className="flex items-center justify-center gap-1.5 py-3"
          style={{ paddingBottom: "env(safe-area-inset-bottom, 12px)" }}
        >
          {photos.map((_, i) => (
            <button
              key={i}
              onClick={() => setWrapped(i)}
              className={`rounded-full transition-all ${
                i === index ? "w-2 h-2 bg-white" : "w-1.5 h-1.5 bg-white/30"
              }`}
            />
          ))}
        </div>
      )}
    </motion.div>
  );
}
